import {usePapaParse} from "react-papaparse";
import React, {useEffect, useState} from "react";
import {Grid, Box, Select, MenuItem, FormControl} from "@mui/material";

//components
import LineSvg from "../../LineSvg";
import CustomLink from "../../Common/CustomLink";
import ModalTrigger from "../../Common/ModalTrigger";
import Samples from "../Samples";

//maps
import mapJSON from "../../../Data/JSON/ChapterOneMap.json";
import mapCSV from "../../../Data/EUMACP/Overview/EU-MACP-Overview-chapter-three-No-of-samples-per-country-and-food-commodity.csv";
import ChapterThreeMap from "../../Maps/ChapterThreeMap";

import makeStyles from "@mui/styles/makeStyles";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    maxHeight: "400px",
  },
}));

const FirstRow = () => {
  const [countryData, setCountryData] = useState(undefined);

  const [selectCounrty, setSelectCountry] = useState("");
  const [groupCountries, setGroupCountries] = useState({});
  const [countrySelectStatus, setCountrySelectStatus] = useState({
    selected: false,
    country: undefined,
  });

  const [mapData, setMapData] = useState([]);


  const handleCountryClick = (evt) => {
    setCountryData(evt);
    if (evt === undefined) {
      handleReadString();
      setSelectCountry("");
    } else {
      setSelectCountry(evt.country);
    }
  };
  const {readString} = usePapaParse();

  const handleReadString = () => {
    readString(mapCSV, {
      complete: (results, file) => {
        const initialData = {country: "All", data: []};
        const foodCommodities = results.data.map((d) => d["Country of origin"]);
        const unique = [...new Set(foodCommodities)];

        unique.forEach((fc) => {
          if (fc) {
            initialData.data.push({
              country: "All",
              "Country of origin": fc,
              Samples: 0,
            });
          }
        });
        results.data.forEach((d) => {
          const found = initialData.data.find(
            (da) => da["Country of origin"] === d["Country of origin"]
          );
          if (found) {
            found["Samples"] += d["Samples"];
          }
        });
        setCountryData(initialData);
      },
      download: true,
      header: true,
      dynamicTyping: true,
      error: (error, file) => {
        console.log("Error while parsing:", error, file);
      },
    });
  };

  const handleReadMapString = () => {
    readString(mapCSV, {
      complete: (results, file) => {
        mapJSON.features.map((j) => {
          let sum = 0;
          results.data
          .filter((d) => d["Country"] === j.properties["NAME"])
          .forEach((f) => {
            sum += f["Samples"];
          });

          const hasData = sum > 0;

          j.properties = {
            ...j.properties,
            sum: hasData ? sum : null, // or undefined
          };
          return j;
        });

        setMapData({json: mapJSON, conv: results.data});
      },
      download: true,
      header: true,
      dynamicTyping: true,
      error: (error, file) => {
        console.log("Error while parsing:", error, file);
      },
    });
  };

  useEffect(() => {
    handleReadString();
    handleReadMapString()
  }, []);

  useEffect(() => {
    var grouped = _.mapValues(_.groupBy(mapData.conv, "Country"), (clist) =>
      clist.map((country) => _.omit(country, "Country"))
    );
    setGroupCountries(grouped);
  }, [mapData]);

  return (
    <Grid
      item
      container
      direction={ {xs: "column", sm: "column", md: "row"} }
      sx={ {marginTop: {xs: "50px", sm: "50px", md: "90px"}} }
      className="one-map-wrapper"
      style={
        {
          position: "relative",
        }
      }
    >
      <Grid item xs display="flex" flexDirection="column" p={ 3 }>
        <Box
          display="flex"
          flexDirection="column"
          sx={ {
            paddingBottom: {md: "1rem"},
          } }
        >
          <div className="lineSvg">
            <LineSvg/>
          </div>
          <span>EU MACP RESULTS</span>
          <h3>Results by reporting country</h3>
          <p className="paragraph">
            In <CustomLink
            href="https://eur-lex.europa.eu/eli/reg_impl/2019/1793/oj/eng"
            text="Regulation (EU) 2019/1793"
          /> on the temporary increase of import controls, certain foods are subject to an increased frequency of official controls for certain pesticides at border control posts (BCPs) or at control points (CPs) at their entry into the EU territory.
          </p>
          <p className="paragraph">
            Reporting countries have the legal obligation to report data from these controls to the European Commission. Thus, EFSA does not receive the entire set of data, which in 2024 amounted to 39,433 samples. Of those samples, 38.3% contained no quantifiable residues, 56.2% contained residues within legally permitted levels, and 5.5% showed MRL exceedances. Of these latter samples, 3.6% were found to be non-compliant when considering measurement uncertainty.
          </p>
        </Box>
        <Box sx={ {display: "block"} } className="dataBox1">
          <FormControl sx={ {mb: 1, minWidth: 120, backgroundColor: "white"} }>
            <Select
              value={ selectCounrty }
              onChange={ (e) => {
                const state = {
                  country: e.target.value,
                  data: mapData.conv.filter(
                    (d) => d["Country"] === e.target.value
                  ),
                };

                if (e.target.value === "") {
                  setSelectCountry("");
                  setCountrySelectStatus({
                    selected: false,
                    country: undefined,
                  });
                  handleCountryClick(undefined);
                } else {
                  setCountrySelectStatus(() => {
                    setSelectCountry(e.target.value);
                    handleCountryClick(state);
                    return {
                      selected: true,
                      country: e.target.value,
                    };
                  });
                }


              } }
              sx={ [
                {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#22294D",
                  },

                  width: "100%",
                },
              ] }
              displayEmpty
            >
              <MenuItem value="">
                All countries
              </MenuItem>
              { groupCountries &&
              Object.keys(groupCountries).map((c, idx) => {
                if (c !== "null") {
                  return (
                    <MenuItem key={ idx } value={ c }>
                      { c }
                    </MenuItem>
                  );
                }
              }) }
            </Select>
          </FormControl>
          <Samples data={ countryData } chunkSize={ 4 }/>
        </Box>
      </Grid>
      <Grid item xs className="one-two" p={ 3 }>
        <ChapterThreeMap
          onCountryClick={ handleCountryClick }
          json={ mapJSON }
          csv={ mapCSV }
          data={ countryData }
        />

        <Box
          className="mapTitle"
          sx={ {display: {xs: "none", md: "contents"}, marginTop: "2rem"} }
        >
        </Box>
      </Grid>
      <Grid
        item
        p={ 3 }
        xs
        sx={ {
          display: {xs: "block", sm: "none", md: "none"},
        } }
        className="one-three"
      >
        <Samples p={ 5 } data={ countryData } chunkSize={ 7 }/>
      </Grid>
    </Grid>
  );
};

export default FirstRow;
